import React, { useEffect, useMemo, useState } from 'react';
import Button from '../../../components/Atoms/Button';

/**
 * The one dialog on the Connected services screen.
 *
 * It has three modes and they all ask for the SAME thing at the bottom — the
 * user's own LabelFlow password — because every /auth_management/services/*
 * route proves the actor with it before it reads or writes anything. `user_id`
 * alone is not actor proof: it is a guessable string that a second person could
 * hold, so the server refuses to act on it. One dialog rather than three keeps
 * that single requirement stated once, in one place.
 *
 *   connect    — the service's own credentials + the actor password
 *   disconnect — confirmation + the actor password
 *   recheck    — the actor password alone
 *
 * Nothing typed here is ever written to storage. The parent mounts this
 * component fresh for each action (a changing React key) and unmounts it on
 * close, so the credentials live in this component's state and nowhere else,
 * for as long as the dialog is open and not a moment longer.
 */

/* A provider whose descriptor forgot to declare credentialFields would
   otherwise render a dialog with nothing to type into and a Connect button that
   cannot possibly succeed. Fall back to the pair every provider needs. */
const FALLBACK_FIELDS = [
  { name: 'identifier', label: 'Account email', type: 'email', placeholder: 'you@example.com' },
  { name: 'password', label: 'Account password', type: 'password', placeholder: '' },
];

const TITLE = {
  connect: (label) => `Connect ${label}`,
  disconnect: (label) => `Disconnect ${label}`,
  recheck: () => 'Re-check your services',
};

/**
 * Map whatever a descriptor declared onto the two slots
 * `/auth_management/services/connect` actually has.
 *
 * `credentialFields` is free-form per provider — ProWatchly happens to name its
 * two `identifier` and `password`, and a provider added later need not. The
 * request body cannot grow a third field, so the pair is resolved by TYPE (the
 * first `password` field is the password, the first field that is not is the
 * identifier) with the literal names as the fallback. Reading `values.identifier`
 * and `values.password` directly — which the caller used to do — sends two empty
 * strings for any provider that spelled them anything else, i.e. a form that
 * fills in correctly and submits nothing.
 */
const resolveCredential = (fields, values) => {
  const passwordField = fields.find((field) => field.type === 'password');
  const identifierField = fields.find((field) => field.type !== 'password');
  const pick = (field, literal) => {
    const key = field?.name || literal;
    const value = values[key];
    return value == null ? String(values[literal] || '') : String(value);
  };
  return {
    identifier: pick(identifierField, 'identifier').trim(),
    password: pick(passwordField, 'password'),
  };
};

const CONFIRM_LABEL = {
  connect: 'Connect',
  disconnect: 'Disconnect',
  recheck: 'Re-check',
};

const ConnectServiceDialog = ({
  mode = 'connect',
  provider = null,
  target = null,
  pending = false,
  error = '',
  onSubmit,
  onClose,
}) => {
  const label = provider?.label || target?.label || 'this service';

  const fields = useMemo(() => {
    if (mode !== 'connect') return [];
    const declared = Array.isArray(provider?.credentialFields) ? provider.credentialFields : [];
    return declared.length ? declared : FALLBACK_FIELDS;
  }, [mode, provider]);

  const [values, setValues] = useState({});
  const [actorPassword, setActorPassword] = useState('');
  const [localError, setLocalError] = useState('');

  /* Escape closes it — the idiom every other dialog and drawer in this app
     already follows. Gated on `pending` for the same reason the backdrop is: a
     credential is in flight, and dismissing the dialog would leave the caller
     with no answer to a call that is still going to land. */
  useEffect(() => {
    const onKey = (event) => {
      if (event.key !== 'Escape' || pending) return;
      event.stopPropagation();
      if (onClose) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [pending, onClose]);

  const setField = (name, value) => {
    setValues((previous) => ({ ...previous, [name]: value }));
    setLocalError('');
  };

  const handleSubmit = (event) => {
    if (event) event.preventDefault();
    if (pending) return;

    const missing = fields.find((field) => !String(values[field.name] || '').trim());
    if (missing) {
      setLocalError(`${missing.label} is required.`);
      return;
    }
    if (!actorPassword) {
      setLocalError('Enter your LabelFlow password to confirm it is you.');
      return;
    }

    setLocalError('');
    onSubmit({
      fields: values,
      credential: resolveCredential(fields, values),
      actorPassword,
    });
  };

  const shownError = localError || error;

  return (
    <div
      className="modal fade show consvc-dialog"
      tabIndex="-1"
      role="dialog"
      aria-modal="true"
      aria-labelledby="consvc-dialog-title"
      onClick={pending ? undefined : onClose}
    >
      <div className="modal-dialog" role="document" onClick={(event) => event.stopPropagation()}>
        <div className="modal-content">
          <form onSubmit={handleSubmit}>
            <div className="modal-header">
              <h5 className="modal-title h5 mb-0" id="consvc-dialog-title">
                {(TITLE[mode] || TITLE.connect)(label)}
              </h5>
            </div>

            <div className="modal-body">
              {mode === 'connect' && (
                <p className="consvc-dialog__help">
                  {provider?.connectHelp
                    || `Enter the email and password you sign in to ${label} with. We check them once and store only the account id they prove.`}
                </p>
              )}

              {mode === 'disconnect' && (
                <p className="consvc-dialog__help">
                  This removes the link between <strong>{label}</strong> and this LabelFlow
                  workspace. {target?.name ? <><strong>{target.name}</strong> will no longer be </> : 'It will no longer be '}
                  offered as an export target here.
                </p>
              )}

              {mode === 'recheck' && (
                <p className="consvc-dialog__help">
                  We will sign in to each configured service again with your LabelFlow password
                  and rebuild this list from what answers. Use this after a site has been set up
                  for you, or when a link on this screen is shown as not recently confirmed.
                </p>
              )}

              {shownError ? <div className="consvc-dialog__error" role="alert">{shownError}</div> : null}

              {fields.map((field) => (
                <div className="consvc-dialog__field" key={field.name}>
                  <label className="consvc-dialog__label" htmlFor={`consvc-field-${field.name}`}>
                    {field.label}
                  </label>
                  <input
                    id={`consvc-field-${field.name}`}
                    className="form-control"
                    type={field.type || 'text'}
                    value={values[field.name] || ''}
                    placeholder={field.placeholder || ''}
                    disabled={pending}
                    autoComplete="off"
                    onChange={(event) => setField(field.name, event.target.value)}
                  />
                </div>
              ))}

              {mode === 'connect' && (
                <div className="consvc-dialog__note">
                  <i className="fa fa-shield-alt mr-1" aria-hidden="true"></i>
                  This credential is used once, to prove the account is yours. It is not stored —
                  only the account id it proves is kept.
                </div>
              )}

              {mode === 'disconnect' && (
                <div className="consvc-dialog__note consvc-dialog__note--plain">
                  <i className="fa fa-info-circle mr-1" aria-hidden="true"></i>
                  {provider?.disconnectNote
                    || `Disconnecting only removes the link here. Nothing changes on ${label} — no account, user or setting there is touched.`}
                </div>
              )}

              <div className="consvc-dialog__actor">
                <label className="consvc-dialog__label" htmlFor="consvc-actor-password">
                  Your LabelFlow password
                </label>
                <input
                  id="consvc-actor-password"
                  className="form-control"
                  type="password"
                  value={actorPassword}
                  disabled={pending}
                  autoComplete="off"
                  onChange={(event) => { setActorPassword(event.target.value); setLocalError(''); }}
                />
                <p className="consvc-dialog__help mb-0 mt-1">
                  Asked for every time, because knowing this workspace&apos;s address is not proof
                  that you are the person signed in to it.
                </p>
              </div>
            </div>

            <div className="modal-footer">
              <Button variant="secondary" size="sm" disabled={pending} onClick={onClose}>
                Cancel
              </Button>
              {/* type="submit" and NO onClick: the atom renders a real <button>, so
                  a click and the Enter key both go through the form's onSubmit
                  exactly once. Wiring onClick as well fires the handler twice. */}
              <Button
                type="submit"
                variant={mode === 'disconnect' ? 'danger' : 'primary'}
                size="sm"
                disabled={pending}
              >
                {pending
                  ? <><i className="fa fa-spinner fa-spin mr-1" aria-hidden="true"></i>Working…</>
                  : (CONFIRM_LABEL[mode] || 'Confirm')}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ConnectServiceDialog;
